import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ExchangeProjectContextBriefDto,
  ExchangeProjectContextItemDto,
} from '@inference.engine/api-client';
import clsx from 'clsx';
import { ArtifactKindIcon } from '@/entities/artifact';
import {
  AttachmentAddButton,
  AttachmentChip,
  AttachmentImageChip,
  AttachmentRemoveButton,
  ProjectContextFileChip,
} from '../../AttachmentChip';
import { BriefSnapshotPreviewDialog } from './BriefSnapshotPreviewDialog';
import type { ChatMessageProjectContextProps } from './ChatMessageProjectContext.types';
import { useChatMessageProjectContext } from './useChatMessageProjectContext';

// History chips are the roomier `lg` size (46px); image thumbnails match it.
const HISTORY_THUMBNAIL_SIZE = 46;

interface TrailingActionProps {
  item: ExchangeProjectContextItemDto;
  isReferenced: (id: string) => boolean;
  onReference: (item: ExchangeProjectContextItemDto) => void;
  onRemove: (id: string) => void;
}

// Module-scope: used 3 times per `projectContext.items.map` iteration
// (one per item-kind branch), closes over only the 3 values threaded as props.
function TrailingAction({ item, isReferenced, onReference, onRemove }: TrailingActionProps) {
  const { t } = useTranslation('chats');

  return isReferenced(item.id) ? (
    <AttachmentRemoveButton
      ariaLabel={t('chats.chatMessageProjectContext.removeFromNextMessage', {
        title: item.title,
      })}
      testId="remove-context-from-message-button"
      onClick={() => onRemove(item.id)}
    />
  ) : (
    <AttachmentAddButton
      ariaLabel={t('chats.chatMessageProjectContext.addToNextMessage', { title: item.title })}
      testId="add-context-to-message-button"
      onClick={() => onReference(item)}
    />
  );
}

/**
 * Renders the project-context chips frozen onto a sent message, beside the
 * file chips. Each chip behaves like an attached file: re-attach it to the next
 * message or drop it again, and click a brief to preview its snapshot content.
 * Uses the shared AttachmentChip so files and project context read identically.
 */
export const ChatMessageProjectContext = (props: ChatMessageProjectContextProps) => {
  const { projectContext, type } = props;
  const { isReferenced, reference, remove, briefById } =
    useChatMessageProjectContext(projectContext);
  const [previewBrief, setPreviewBrief] = useState<ExchangeProjectContextBriefDto | null>(null);

  if (projectContext.items.length === 0) return null;

  return (
    <>
      <div
        className={clsx(
          'flex flex-wrap gap-2 py-1.5',
          type === 'user' ? 'justify-end' : 'justify-start',
        )}
      >
        {projectContext.items.map((item) => {
          // Image artifacts render as a clickable thumbnail (resolved fresh from
          // the artifact id), like an attached image file.
          if (item.kind === 'image') {
            return (
              <AttachmentImageChip
                key={item.id}
                artifactId={item.artifactId}
                filename={item.title}
                sizePx={HISTORY_THUMBNAIL_SIZE}
                fallback={<ArtifactKindIcon kind={item.kind} />}
                trailing={
                  <TrailingAction
                    item={item}
                    isReferenced={isReferenced}
                    onReference={reference}
                    onRemove={remove}
                  />
                }
              />
            );
          }

          // File-backed artifacts (e.g. PDFs) open in a new tab, like attached files.
          if (item.kind === 'file') {
            return (
              <ProjectContextFileChip
                key={item.id}
                artifactId={item.artifactId}
                name={item.title}
                size="lg"
                leading={<ArtifactKindIcon kind={item.kind} />}
                trailing={
                  <TrailingAction
                    item={item}
                    isReferenced={isReferenced}
                    onReference={reference}
                    onRemove={remove}
                  />
                }
              />
            );
          }

          const brief = item.kind === 'brief' ? briefById.get(item.id) : undefined;
          return (
            <AttachmentChip
              key={item.id}
              name={item.title}
              size="lg"
              leading={<ArtifactKindIcon kind={item.kind} />}
              onClick={brief ? () => setPreviewBrief(brief) : undefined}
              trailing={
                <TrailingAction
                  item={item}
                  isReferenced={isReferenced}
                  onReference={reference}
                  onRemove={remove}
                />
              }
            />
          );
        })}
      </div>
      <BriefSnapshotPreviewDialog brief={previewBrief} onClose={() => setPreviewBrief(null)} />
    </>
  );
};
